import * as React from 'react';

import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { DataSourcesActionType } from 'src/actions/data-sources/actionTypes';
import { connectionSelector } from 'src/selectors/connection';
import * as copy from 'src/templates/copy';
import { useCanWriteSpecificDataSource } from 'src/utils/hooks/useDesignerPermissions';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export const useRefreshDataSourceTableMetadata = (
  dataSourceId: string | undefined,
  tableName: string | null,
  onError: (message: string) => void,
  onRefreshComplete: () => void
) => {
  const dispatch = useReduxDispatch();
  const connection = useReduxSelector(connectionSelector);
  const canWriteSpecificDataSource = useCanWriteSpecificDataSource();
  const isMounted = useIsMounted();

  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const refreshPreview = React.useCallback(async () => {
    const shouldRefreshMetadata =
      tableName &&
      dataSourceId &&
      connection &&
      canWriteSpecificDataSource(dataSourceId);

    if (!shouldRefreshMetadata) {
      onRefreshComplete();
      return;
    }

    setIsRefreshing(true);
    const result =
      await dataSourcesStardogRequestDispatchers.refreshDataSourceTableMetadata(
        dataSourceId,
        tableName,
        connection
      );
    if (!isMounted.current) {
      return;
    }
    if (
      result.type ===
      DataSourcesActionType.REFRESH_DATA_SOURCE_TABLE_METADATA_FAILURE
    ) {
      console.warn('Failed to refresh data source metadata', result);
      onError(copy.components.virtualGraphPreviewDialog.metadataRefreshFailed);
      setIsRefreshing(false);
      return;
    }

    // Re-read the options too so a changed previews-disabled flag is picked up
    // on refresh (requestDataSourceInfo preserves the cached options).
    await dispatch(
      refreshDataSourceTables(dataSourceId, { includeOptions: true })
    );
    if (!isMounted.current) {
      return;
    }

    onRefreshComplete();
    setIsRefreshing(false);
  }, [
    canWriteSpecificDataSource,
    connection,
    dataSourceId,
    dispatch,
    isMounted,
    onError,
    onRefreshComplete,
    tableName,
  ]);

  return { isRefreshing, refreshPreview };
};
